import { clearLdProductNowAnswers } from '@/lib/ldProductNowStorage';
import { clearSetBLdBrandOpinionAnswers } from '@/lib/setBLdBrandOpinionStorage';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import { clearOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { clearPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { clearPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { writeSetAProfileAnswers } from '@/lib/setAProfileStorage';
import { clearWhyNotLdAnswers } from '@/lib/whyNotLdStorage';
import { verifyOtp } from '@/api/otp';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanel, SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { isMaxSubmissionsReached } from '@/lib/maxSubmissions';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setOtpDraft } from '@/stores/appSlice';
import { selectOtpDraft, selectSurveyScreening } from '@/stores/selectors';
import { displayFullMalaysiaMobile } from '@/utils/malaysiaMobile';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const OTP_VERIFY_MIN_WAIT_MS = 5_000;

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300 tabular-nums tracking-wide';

export default function Otp() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const { mobileLocalDigits, customerUuid, otpSentAtMs } = useAppSelector(selectOtpDraft);
  const [otpCode, setOtpCode] = useState('');
  const [otpWarning, setOtpWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitSecondsLeft, setWaitSecondsLeft] = useState(0);

  useEffect(() => {
    const tick = () => {
      if (otpSentAtMs == null) {
        setWaitSecondsLeft(0);
        return;
      }
      const msLeft = OTP_VERIFY_MIN_WAIT_MS - (Date.now() - otpSentAtMs);
      setWaitSecondsLeft(Math.max(0, Math.ceil(msLeft / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 300);
    return () => window.clearInterval(id);
  }, [otpSentAtMs]);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/phone" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/phone" replace />;
  }

  const handleSubmit = async () => {
    if (waitSecondsLeft > 0) return;
    const trimmed = otpCode.trim();
    if (!trimmed) {
      setOtpWarning(t.otp.emptyWarning);
      return;
    }
    setOtpWarning(null);
    setIsSubmitting(true);
    try {
      const result = await verifyOtp({ customerUuid, otp: trimmed });
      if (result.ok === false) {
        if (isMaxSubmissionsReached(result)) {
          navigate('/custcompleted', { replace: true });
          return;
        }
        setOtpWarning(result.error);
        return;
      }

      dispatch(
        setOtpDraft({
          mobileLocalDigits,
          customerUuid,
          otpSentAtMs,
          otpVerified: true,
          submissionIndex: result.submissionIndex,
          questionnaireSet: result.questionnaireSet,
        })
      );

      clearPrevBrandAnswers();
      clearPrevBrandAnswerAnswers();
      clearOtherPrevBrandAnswers();
      clearOtherPrevBrandAnswerAnswers();
      clearWhyNotLdAnswers();
      clearLdProductNowAnswers();
      clearSetBLdBrandOpinionAnswers();

      if (result.questionnaireSet === 'A') {
        navigate('/survey/set-a/profile');
        return;
      }

      if (!result.existingProfile) {
        setOtpWarning(t.otp.profileNotFoundWarning);
        return;
      }

      writeSetAProfileAnswers(result.existingProfile);
      navigate('/ldproductnow');
    } catch {
      setOtpWarning(t.otp.verifyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto mt-[20vh] flex w-full max-w-full flex-col py-6">
          <SurveyContentPanelInset>
            <SurveyQuestionPanel>
              <p className="m-0 break-words text-left text-[16px] leading-snug text-white sm:text-[17px]">
                <span className="block">{t.otp.sentMessage}</span>
                <span className="mt-1 block font-semibold tabular-nums">
                  {displayFullMalaysiaMobile(mobileLocalDigits)}
                </span>
              </p>
            </SurveyQuestionPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4">
            {waitSecondsLeft > 0 ? (
              <p className="m-0 text-center text-sm text-white">
                {t.otp.waitBeforeSubmit.replace(/\{\{seconds\}\}/g, String(waitSecondsLeft))}
              </p>
            ) : (
              <p className="m-0 text-center text-sm text-white">{t.otp.readyToSubmit}</p>
            )}
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-6">
            <SurveyContentPanel className="!p-4">
              <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2">
                <label htmlFor="otp-code" className="text-center text-[16px] font-medium text-white">
                  {t.otp.otpCodeLabel}
                </label>
                <input
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={lightInputClassName}
                />
              </div>
            </SurveyContentPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {otpWarning != null ? <SurveyValidationAlert>{otpWarning}</SurveyValidationAlert> : null}
              <SurveyButton disabled={isSubmitting || waitSecondsLeft > 0} onClick={() => void handleSubmit()}>
                {t.otp.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
